/**
 * Payout run-sheet table; expand a row for `lines[]` when the response carries them (the
 * per-tenant list does; the fleet run sheet omits them). TMMPDM-0017: the tenant cell links by
 * `tenantSlug` — no tenant join.
 * TMMPDM-0037 (tmm-api 0099): a `CHARGED` settlement shows "Charged to card" and its charge's
 * status, and never a Record action; a reseller payee links to its Settlements tab. The lines
 * table moved to `StatementLinesTable` (shared with the Settlements tab).
 * TMMPDM-0035: sticky column headers; `onClearFilters` makes an empty sheet the filtered-empty state.
 * TMMPDM-0095 / 0096: `frozen` (the caller, from `usePayoutActionsRetired`) hides Record payout and
 * Release hold; the rows stay, and each payee's name links its own Ledger.
 */
import { Fragment, useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useCopy } from '@/hooks/useCopy';
import { EmptyState } from '@/components/page';
import { Amount, stickyTableContainerSx } from '@/components/ui';
import { formatDate } from '@/format';
import type {
  PayeeKind,
  PayoutHold,
  PayoutMethod,
  PayoutStatementView,
  PayoutStatus,
} from '@/api/tmm/types';
import { ResellerChargeChip } from '../resellers/ResellerChargeChip';
import { StatementLinesTable } from './StatementLinesTable';
import { RecordPayoutAction } from './RecordPayoutDialog';
import { ReleaseHoldDialog } from '../affiliates/AffiliateLifecycleDialogs';
import { useNotify } from '@/components/page';
import { StatusChip } from '@/status';
import { affiliateLedgerPath, resellerLedgerPath } from '../ledger/ledger';

interface RunSheetTableProps {
  rows: PayoutStatementView[] | null;
  loading: boolean;
  locale: string;
  emptyLabel: string;
  /** A filter is narrowing the sheet: an empty one offers Clear filters (TBL-005). */
  onClearFilters?: (() => void) | undefined;
  onRecord: (row: PayoutStatementView) => void;
  /** After Release hold succeeds: the caller re-reads the sheet. */
  onHoldReleased: () => void;
  /**
   * TMMPDM-0095 / 0096: the old actions are retired, so this is frozen history: Record payout and
   * Release hold are gone, and a reseller payee links to its Ledger.
   */
  frozen?: boolean;
  /** An old action answered 410 `PAYOUTS_MOVED_TO_LEDGER`: the caller re-reads. */
  onMovedToLedger?: () => void;
}

const PAYEE_FALLBACK: Record<PayeeKind, string> = {
  RESELLER: 'Reseller',
  AFFILIATE: 'Affiliate',
};

const METHOD_FALLBACK: Record<PayoutMethod, string> = {
  MANUAL: 'Manual',
  STRIPE_CONNECT_MANUAL: 'Stripe Connect (manual payouts)',
  STRIPE_CONNECT: 'Stripe Connect (automatic payouts)',
};

const HOLD_FALLBACK: Record<PayoutHold, string> = {
  HOLD_TAX_DOCS: 'Tax docs',
  HOLD_STRIPE_CONNECT: 'Stripe Connect',
  HOLD_TRANSFER_CURRENCY: 'Transfer currency',
  HOLD_TRANSFER_CAP: 'Transfer cap',
  HOLD_RESELLER_UNPAID: 'Reseller charge unpaid',
  HOLD_AFFILIATE_REVIEW: 'Held for review (affiliate ended for cause)',
};

const STATUS_FALLBACK: Record<PayoutStatus, string> = {
  RETAINED: 'Retained',
  PENDING: 'Pending',
  CARRIED: 'Carried',
  PAID: 'Paid',
  CHARGED: 'Charged',
};

export function RunSheetTable({
  rows,
  loading,
  locale,
  emptyLabel,
  onClearFilters,
  onRecord,
  onHoldReleased,
  frozen = false,
  onMovedToLedger,
}: RunSheetTableProps) {
  const { t } = useCopy();
  const notify = useNotify();
  const [expanded, setExpanded] = useState<number | null>(null);
  const [releasing, setReleasing] = useState<PayoutStatementView | null>(null);

  return (
    <TableContainer component={Paper} variant="outlined" sx={stickyTableContainerSx}>
      <Table size="small" stickyHeader aria-label={t('payouts.tableAria', 'Payout run sheet')}>
        <TableHead>
          <TableRow>
            <TableCell />
            <TableCell>{t('payouts.col.payee', 'Payee')}</TableCell>
            <TableCell>{t('payouts.col.tenant', 'Tenant')}</TableCell>
            <TableCell>{t('payouts.col.period', 'Period')}</TableCell>
            <TableCell align="right">{t('payouts.col.gross', 'Gross')}</TableCell>
            <TableCell align="right">{t('payouts.col.carriedIn', 'Carried in')}</TableCell>
            <TableCell align="right">{t('payouts.col.payable', 'Payable')}</TableCell>
            <TableCell>{t('payouts.col.method', 'Method')}</TableCell>
            <TableCell>{t('payouts.col.hold', 'Hold')}</TableCell>
            <TableCell>{t('payouts.col.status', 'Status')}</TableCell>
            <TableCell>{t('payouts.col.paid', 'Paid')}</TableCell>
            <TableCell>{t('payouts.col.actions', 'Actions')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows === null ? (
            <TableRow>
              <TableCell colSpan={12} align="center" sx={{ py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  {loading ? t('common.loading', 'Loading') : null}
                </Typography>
              </TableCell>
            </TableRow>
          ) : rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={12} align="center" sx={{ py: 4 }}>
                <EmptyState
                  title={emptyLabel}
                  {...(onClearFilters ? { filtered: { onClear: onClearFilters } } : {})}
                />
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => {
              const lines = row.lines;
              const open = lines !== undefined && expanded === row.id;
              return (
                <Fragment key={row.id}>
                  <TableRow hover>
                    <TableCell>
                      {lines !== undefined ? (
                        <IconButton
                          size="small"
                          aria-label={
                            open
                              ? t('payouts.collapse', 'Collapse lines')
                              : t('payouts.expand', 'Expand lines')
                          }
                          onClick={() => setExpanded(open ? null : row.id)}
                        >
                          {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={t(`payouts.payee.${row.payeeKind}`, PAYEE_FALLBACK[row.payeeKind])}
                        sx={{ mr: 0.5 }}
                      />
                      {row.payeeKind === 'RESELLER' ? (
                        <Link
                          component={RouterLink}
                          to={
                            frozen
                              ? resellerLedgerPath(row.payeeId)
                              : `/partners/${encodeURIComponent(String(row.payeeId))}?tab=settlements`
                          }
                          variant="body2"
                          underline="hover"
                        >
                          {row.payeeName ?? String(row.payeeId)}
                        </Link>
                      ) : frozen ? (
                        <Link
                          component={RouterLink}
                          to={affiliateLedgerPath(row.tenantSlug, row.payeeId)}
                          variant="body2"
                          underline="hover"
                        >
                          {row.payeeName ?? String(row.payeeId)}
                        </Link>
                      ) : (
                        <Typography variant="body2" component="span">
                          {row.payeeName ?? String(row.payeeId)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {row.payeeKind === 'RESELLER' ? (
                        <>
                          <Typography variant="body2">
                            {t('payouts.allTenants', 'All tenants')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" component="div">
                            {t('payouts.paidViaPrefix', 'paid via')}{' '}
                            <Link
                              component={RouterLink}
                              to={`/partners/by-tenant/${encodeURIComponent(row.tenantSlug)}?tab=affiliatePayouts`}
                              underline="always"
                            >
                              {row.tenantSlug}
                            </Link>
                          </Typography>
                        </>
                      ) : (
                        <Link
                          component={RouterLink}
                          to={`/partners/by-tenant/${encodeURIComponent(row.tenantSlug)}?tab=affiliatePayouts`}
                          variant="body2"
                          underline="hover"
                        >
                          {row.tenantSlug}
                        </Link>
                      )}
                    </TableCell>
                    <TableCell>
                      {formatDate(row.periodStart, locale)} – {formatDate(row.periodEnd, locale)}
                    </TableCell>
                    <TableCell align="right">
                      <Amount minor={row.grossMinor} currency={row.currency} locale={locale} />
                    </TableCell>
                    <TableCell align="right">
                      <Amount minor={row.carriedInMinor} currency={row.currency} locale={locale} />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        <Amount minor={row.payableMinor} currency={row.currency} locale={locale} />
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {t(`payouts.method.${row.payoutMethod}`, METHOD_FALLBACK[row.payoutMethod])}
                    </TableCell>
                    <TableCell>
                      {row.hold !== undefined ? (
                        <StatusChip
                          tone="warning"
                          label={t(`payouts.hold.${row.hold}`, HOLD_FALLBACK[row.hold])}
                        />
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>
                      {row.payoutStatus === 'CHARGED' ? (
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                          <Typography variant="body2" component="span">
                            {t('resellers.settlements.outcome.CHARGED', 'Charged to card')}
                          </Typography>
                          {row.charge !== undefined ? (
                            <ResellerChargeChip status={row.charge.status} />
                          ) : null}
                        </Stack>
                      ) : (
                        <StatusChip
                          status={row.payoutStatus}
                          label={t(
                            `payouts.status.${row.payoutStatus}`,
                            STATUS_FALLBACK[row.payoutStatus],
                          )}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {row.payoutStatus === 'PAID'
                        ? [row.payoutAt ? formatDate(row.payoutAt, locale) : null, row.payoutRef]
                            .filter(Boolean)
                            .join(' · ')
                        : '—'}
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.5} sx={{ alignItems: 'flex-start' }}>
                        {!frozen && row.payoutStatus === 'PENDING' ? (
                          <RecordPayoutAction row={row} onRecord={onRecord} />
                        ) : null}
                        {!frozen && row.hold === 'HOLD_AFFILIATE_REVIEW' ? (
                          <Button
                            size="small"
                            aria-label={t('affiliates.release.row', 'Release hold for {{name}}', {
                              name: row.payeeName ?? `#${row.payeeId}`,
                            })}
                            onClick={() => setReleasing(row)}
                          >
                            {t('affiliates.release.action', 'Release hold…')}
                          </Button>
                        ) : null}
                      </Stack>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={12} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
                      <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 1 }}>
                          <StatementLinesTable lines={lines ?? []} locale={locale} />
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </Fragment>
              );
            })
          )}
        </TableBody>
      </Table>
      {releasing !== null ? (
        <ReleaseHoldDialog
          open
          slug={releasing.tenantSlug}
          affiliateId={releasing.payeeId}
          name={releasing.payeeName ?? `#${releasing.payeeId}`}
          ledgerTo={affiliateLedgerPath(releasing.tenantSlug, releasing.payeeId)}
          onMovedToLedger={onMovedToLedger}
          onClose={() => setReleasing(null)}
          onReleased={() => {
            setReleasing(null);
            notify.success(
              t('affiliates.release.done', 'Hold released. Unpaid statements can be paid.'),
            );
            onHoldReleased();
          }}
        />
      ) : null}
    </TableContainer>
  );
}
